import { ReactNode, useState } from "react";
import { IconInfoCircle } from "@tabler/icons-react";
import clsx from "clsx";

import UploadArea from "./UploadArea";
import UploadCard from "./UploadCard";
import { UploadCardProps } from "./UploadCard/UploadCard";
import { UploadAreaProps } from "./UploadArea/UploadArea";

type AcceptedUploadCardProps = Omit<Partial<UploadCardProps>, "file" | "onRemove">;
interface FileUploadProps {
  className?: string;
  title?: ReactNode;
  description?: ReactNode;
  uploadTitle: ReactNode;
  uploadLabel: ReactNode;
  icon?: ReactNode;
  value: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  fullWidth?: boolean;
  errors?: (string | undefined | null)[];
  UploadAreaProps?: Omit<Partial<UploadAreaProps>, "onChange">
  UploadCardProps?: ((file: File) => AcceptedUploadCardProps) | AcceptedUploadCardProps
  accept?: string[];
}

const FileUpload = ({
  className, title, description, icon, uploadTitle, uploadLabel, disabled, value = [], onChange, maxFiles,
  UploadAreaProps, UploadCardProps, fullWidth = false, errors = [], accept,
}: FileUploadProps) => {
  const addFiles = (newFiles: File[]) => {
    onChange([...value, ...newFiles]);
  };

  const removeFile = (file: File) => {
    onChange(value.filter((f) => f.name !== file.name));
  };

  return (
    <div className={clsx("sds space-y-2", className)}>

      <div className="typography-medium-base">
        <div className="flex items-center space-x-1.5 mb-1">
          {icon && <span className={disabled ? "text-icon-disabled" : "text-icon-bg-base"}>{icon}</span>}
          <span className={disabled ? "text-text-disabled" : "text-text-base"}>{title}</span>
          {/* <IconInfoCircle className={disabled ? "text-icon-disabled" : "text-icon-bg-base"} /> */}
        </div>
        {description && <span className={disabled ? "text-text-disabled" : "text-text-subtle"}>{description}</span>}
      </div>

      {(!maxFiles || value.length < maxFiles) && (
        <UploadArea
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...UploadAreaProps}
          className={clsx(UploadAreaProps?.className, fullWidth && "w-full")}
          title={uploadTitle}
          label={uploadLabel}
          onChange={addFiles}
          disabled={disabled}
          accept={accept ? accept.join(",") : undefined}
        />
      )}
      <div className="space-y-1">
        {value?.map((file, i) => {
          const resolvedUploadCardProps = typeof UploadCardProps === "function" ? UploadCardProps(file) : UploadCardProps;
          return (
            <UploadCard
              type={errors[i] ? "error" : "base"}
              supportiveLabel={errors[i]}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...resolvedUploadCardProps}
              className={clsx(resolvedUploadCardProps?.className, fullWidth && "w-full")}
              file={file}
              onRemove={() => removeFile(file)}
            />
          );
        })}
      </div>
    </div>
  );
};

export default FileUpload;
